import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { getCompanySlug, getPortalUrlFlat } from '../../utils/subdomain';
import { useHostContext } from '../../hooks/useHostContext';
import { cacheCompanyModules } from '../../utils/companyModules';

function shadeColor(hex, percent) {
  if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return '#3E2C6E';
  const num = parseInt(hex.slice(1), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + percent));
  const b = Math.min(255, Math.max(0, (num & 0xff) + percent));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const PORTALS = [
  {
    key: 'company',
    href: '/login',
    icon: '👤',
    title: (c) => c?.name || 'موظف الشركة',
    desc: 'لوحة التحكم والإدارة',
    color: (c) => c?.brand_color || '#3E2C6E',
    useLogo: true,
  },
  {
    key: 'merchant',
    icon: '🏪',
    title: (c) => c?.merchant_app_name || 'تاجر',
    desc: (c) => (c?.merchant_app_name ? 'بوابة التاجر' : 'إدارة طلبيات الشحن'),
    color: () => '#2E7D32',
  },
  {
    key: 'rider',
    icon: '🏍️',
    title: (c) => c?.rider_app_name || 'مندوب التوصيل',
    desc: (c) => (c?.rider_app_name ? 'تطبيق المندوب' : 'استلام وتسليم الطلبات'),
    color: () => '#E65100',
  },
  {
    key: 'customer',
    icon: '📦',
    title: () => 'تتبع الشحنة',
    desc: () => 'تتبع طلبك برقم التتبع',
    color: () => '#1565C0',
    public: true,
  },
];

export default function RolePromptPage() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const hostCtx = useHostContext();
  const slug = hostCtx.companySlug || getCompanySlug();

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    api.getCompanyBySlug(slug)
      .then((data) => {
        setCompany(data);
        if (data) cacheCompanyModules(slug, data);
      })
      .catch(() => console.warn('Failed to load company info'))
      .finally(() => setLoading(false));
  }, [slug]);

  const brandColor = company?.brand_color || '#3E2C6E';
  const brandLight = shadeColor(brandColor, 40);
  const brandDark = shadeColor(brandColor, -30);
  const customHosts = company?.use_custom_domains || company?.domain_company ? company : null;

  function portalUrl(key) {
    if (key === 'company') return '/login';
    const fromApi = company?.portal_urls?.[key];
    if (fromApi) return fromApi;
    if (slug) return getPortalUrlFlat(slug, key, customHosts);
    return key === 'merchant' ? '/m/login' : key === 'rider' ? '/rider/login' : '/';
  }

  return (
    <div style={{ ...styles.page, background: `linear-gradient(145deg, ${brandDark} 0%, ${brandColor} 45%, ${brandLight} 100%)` }}>
      <style>{`
        @keyframes rpFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes rpShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes rpFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .rp-card { animation: rpFadeUp 0.5s ease both; }
        .rp-portal:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.14); }
        .rp-portal:active { transform: translateY(-1px); }
        .rp-customer:hover {
          background: linear-gradient(135deg, #0D47A1 0%, #1565C0 100%) !important;
          transform: translateY(-2px);
          box-shadow: 0 10px 28px rgba(13,71,161,0.45);
          color: #fff !important;
        }
        .rp-customer:hover .rp-customer-title,
        .rp-customer:hover .rp-customer-desc,
        .rp-customer:hover .rp-customer-arrow { color: #fff !important; opacity: 1; }
        @media (max-width: 640px) {
          .rp-grid { grid-template-columns: 1fr !important; }
          .rp-hero-title { font-size: 22px !important; }
        }
      `}</style>

      <div className="rp-card" style={styles.shell}>
        {/* Header */}
        <div style={styles.hero}>
          <div style={styles.logoWrap}>
            {company?.logo_url ? (
              <img src={company.logo_url} alt={company.name} style={styles.logo} />
            ) : (
              <div style={{ ...styles.logoFallback, background: brandColor }}>
                <span style={{ fontSize: 32 }}>🚚</span>
              </div>
            )}
          </div>
          <h1 className="rp-hero-title" style={styles.heroTitle}>
            {loading ? 'جاري التحميل...' : `مرحباً بكم في ${company?.name || 'منصة الشحن'}`}
          </h1>
          <p style={styles.heroSub}>اختر البوابة المناسبة للوصول إلى خدماتك</p>
        </div>

        {/* Portal grid */}
        <div className="rp-grid" style={styles.grid}>
          {loading ? (
            [0, 1, 2].map((i) => (
              <div key={i} style={styles.portalSkeleton} aria-hidden="true" />
            ))
          ) : PORTALS.filter((p) => {
            if (p.key === 'customer') return false;
            if (p.key === 'rider' && company?.riders_enabled === 0) return false;
            return true;
          }).map((portal, i) => {
            const color = portal.color(company);
            const title = portal.title(company);
            const desc = typeof portal.desc === 'function' ? portal.desc(company) : portal.desc;
            return (
              <a
                key={portal.key}
                href={portalUrl(portal.key)}
                className="rp-portal"
                style={{ ...styles.portalCard, animationDelay: `${0.08 * i}s` }}
              >
                <div style={{ ...styles.iconCircle, background: `${color}18`, color }}>
                  {portal.useLogo && company?.logo_url ? (
                    <img src={company.logo_url} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: 26 }}>{portal.icon}</span>
                  )}
                </div>
                <div style={styles.portalText}>
                  <div style={styles.portalTitle}>{title}</div>
                  <div style={styles.portalDesc}>{desc}</div>
                </div>
                <span style={{ ...styles.arrow, color }}>←</span>
              </a>
            );
          })}
        </div>

        {/* Customer portal — public, no login */}
        <a
          href={portalUrl('customer')}
          className="rp-customer"
          style={styles.customerBar}
        >
          <div style={styles.customerIcon}>📦</div>
          <div style={{ flex: 1 }}>
            <div className="rp-customer-title" style={styles.customerTitle}>بوابة الزبون — تتبع الشحنة</div>
            <div className="rp-customer-desc" style={styles.customerDesc}>أدخل رقم التتبع لمتابعة حالة طلبك</div>
          </div>
          <span className="rp-customer-arrow" style={styles.customerArrow}>←</span>
        </a>

        <div style={styles.footer}>
          <span style={styles.footerDot} />
          مدعوم من <strong>فكر نوفا للشحن</strong>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    direction: 'rtl',
    textAlign: 'right',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: "'Tajawal', system-ui, sans-serif",
  },
  shell: {
    background: 'rgba(255,255,255,0.97)',
    borderRadius: 28,
    padding: '36px 32px 28px',
    maxWidth: 520,
    width: '100%',
    boxShadow: '0 24px 64px rgba(0,0,0,0.18), 0 0 0 1px rgba(255,255,255,0.1)',
    backdropFilter: 'blur(12px)',
  },
  hero: { textAlign: 'center', marginBottom: 28 },
  logoWrap: { marginBottom: 16, display: 'flex', justifyContent: 'center' },
  logo: {
    maxWidth: 140, maxHeight: 72, objectFit: 'contain',
    borderRadius: 12, padding: 8, background: '#fff',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  },
  logoFallback: {
    width: 72, height: 72, borderRadius: 18,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
  heroTitle: {
    fontSize: 26, fontWeight: 800, margin: '0 0 8px',
    color: '#1a1a2e', lineHeight: 1.35,
  },
  heroSub: {
    fontSize: 15, color: '#666', margin: 0, lineHeight: 1.6,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
    marginBottom: 16,
  },
  portalSkeleton: {
    height: 84,
    borderRadius: 16,
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'rpShimmer 1.2s ease-in-out infinite',
  },
  portalCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 18px',
    borderRadius: 16,
    background: '#fafafa',
    border: '1px solid #eee',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
  },
  iconCircle: {
    width: 52, height: 52, borderRadius: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  portalText: { flex: 1, minWidth: 0 },
  portalTitle: { fontSize: 16, fontWeight: 700, color: '#1a1a2e' },
  portalDesc: { fontSize: 12, color: '#888', marginTop: 2 },
  arrow: { fontSize: 18, fontWeight: 700, opacity: 0.7 },
  customerBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '16px 18px',
    borderRadius: 16,
    background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
    color: '#fff',
    textDecoration: 'none',
    transition: 'background 0.2s, transform 0.2s, box-shadow 0.2s',
    marginBottom: 20,
    boxShadow: '0 6px 20px rgba(21,101,192,0.3)',
  },
  customerIcon: {
    width: 44, height: 44, borderRadius: 12,
    background: 'rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, flexShrink: 0,
  },
  customerTitle: { fontSize: 15, fontWeight: 700, color: '#fff' },
  customerDesc: { fontSize: 12, color: 'rgba(255,255,255,0.9)', marginTop: 2 },
  customerArrow: { fontSize: 20, fontWeight: 700, color: '#fff', opacity: 0.9 },
  footer: {
    textAlign: 'center', fontSize: 12, color: '#aaa',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  },
  footerDot: {
    width: 6, height: 6, borderRadius: '50%',
    background: '#E0A526', display: 'inline-block',
  },
};